import {useEffect, useMemo, useState} from 'react'
import Typography from '@mui/material/Typography'
import MenuItem from '@mui/material/MenuItem'
import ListItemText from '@mui/material/ListItemText'
import InputAdornment from '@mui/material/InputAdornment'
import {useConfig} from '../../../store/hooks/config'
import {usePointOfInterest} from '../../../store/hooks/pointOfInterest'
import tooltipText from '../../common/TooltipText.json'
import {StyledTooltip} from '../../BreadCrumb/BreadCrumbStyle'
import {
  StyledStack,
  StyledDiv,
  Legend,
  StyledTextField,
  StyledText,
  StyledBox,
  StyledTypographyForMenu,
  StyledBoldLabel,
  StyledFormControlLabel,
  StyledRadioButton,
  StyledSpan,
  StyledCheckBox,
  StyledMenuItem,
  StyledListItemText,
  StyledOffBadge,
  StyledIconContainer,
  StyledDivContainer,
  StyledLockIcon,
  StyledLockBadge
} from './PopulationDensityDropDownStyle'
import {useGeograhicBoundary} from '../../../store/hooks/geographicBoundary'
import {useTableViewContext} from '../../../store/hooks/tableView'
import {Menu, FormControl} from '@mui/material'
import PropTypes from 'prop-types'
import {useFilterBusinessLogic} from '../../../store/hooks/useFilterBusinessLogic'
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined'
import PinDropRoundedIcon from '@mui/icons-material/PinDropRounded'
import HomeIcon from '@mui/icons-material/Home'
import LayersIcon from '@mui/icons-material/Layers'
import {useFocusLayer} from '../../../store/hooks/focusLayer'
import {AnalyticsHandler} from '../../../shared/handlers/analytics/handlers'
import {usePermission} from '../../../store/hooks/permission'
import {getPOIImageUrl, visualOptions} from '../helper'
import {useRolePermissions} from '../../../hooks/useRolesPermissions'
import {selectSelectedGradientView, selectSelectedPopulationViewMode} from '../../../store/slices/focusLayerSlice'
import {useAppSelector} from '../../../store/hooks'
import {ReactComponent as CountPercentageIcon} from '../../../icons/count-percentage-icon.svg'
import {
  selectCurrentUniqueAddressesCount,
  selectUniqueAddressessCount
} from '../../../store/slices/uniqueAddressesCountSlice'
import {useSelector} from 'react-redux'
import FocusLayerMenu from './FocusLayerMenu'
import MenuWithRadioButton from './MenuWithRadioButton'
function updateGeoBoundaryIfLabelMatches(label, boundaryList, targetList) {
  const foundBoundary = boundaryList.find((g) => g.label === label)

  if (foundBoundary) {
    targetList.push(foundBoundary)
  }
}

function setFocusLayer(isAdmin, dataAggregationValue, updateFocusLayer) {
  if (isAdmin || (dataAggregationValue?.includes(4) && dataAggregationValue?.includes(1))) {
    updateFocusLayer(visualOptions[0].value)
  } else if (isAdmin || dataAggregationValue?.includes(4)) {
    updateFocusLayer(visualOptions[0].value)
  } else if (isAdmin || dataAggregationValue?.includes(1)) {
    updateFocusLayer(visualOptions[1].value)
  }
}

const PopulationDensityDropDown = ({showChoropleth, setShowChoropleth}) => {
  PopulationDensityDropDown.propTypes = {
    showChoropleth: PropTypes.bool,
    setShowChoropleth: PropTypes.func
  }

  const {existingPOI, selectedPOI, setSelectedPOI} = usePointOfInterest()
  const {selectedGeoBoundary, setSelectedGeoBoundary, geographicsBoundary} = useGeograhicBoundary()
  const {isChildCareDisabled} = useFilterBusinessLogic()
  const {POIConfig: poiConfig} = useConfig() || {}
  const [layerEL, setLayerEL] = useState(null)
  const [regionEL, setRegionEL] = useState(null)
  const [poiEL, setPoiEL] = useState(null)
  const [visualEL, setVisualEL] = useState(null)
  const [focusHoverEL, setFocusHoverEL] = useState(null)
  const [populationViewEL, setPopulationViewEL] = useState(null)
  const {permission} = usePermission()
  const dataAggregationValue = permission?.healthEquityConditionList?.dataAggregationPermissions
  const updatedGeoBoundry = []
  const [disablePoiFlag, setDisablePoiFlag] = useState(false)
  const {showPopulationDensityTable} = useTableViewContext()
  const {isDeliveryAdmin} = useRolePermissions()

  const {selectedFocusLayer, updateFocusLayer, setSelectedGradientView, setSelectedPopulationViewMode} = useFocusLayer()
  const selectedGradientView = useAppSelector(selectSelectedGradientView)
  const selectedPopulationViewMode = useAppSelector(selectSelectedPopulationViewMode)
  const currentUniqueAddressesCount = useSelector(selectCurrentUniqueAddressesCount)
  const uniqueAddressMaximum = useSelector(selectUniqueAddressessCount) || 1000

  const unifiedViewValue = selectedGradientView === 'population' ? 'counts' : selectedPopulationViewMode

  const getPercentOptions = (boundary) => {
    switch (boundary) {
      case 'state':
        return [
          {label: 'of Total US Population', value: 'selected_of_us_total'},
          {label: 'of Total State Population', value: 'selected_of_state_total'}
        ]
      case 'censustract':
        return [
          {label: 'of Total County Population', value: 'selected_of_county'},
          {label: 'of Total Census Tract Population', value: 'selected_of_census_tract'}
        ]
      case 'county':
        return [
          {label: 'of Total State Population', value: 'selected_of_state_total'},
          {label: 'of Total County Population', value: 'selected_of_county'}
        ]
      case 'congressionaldistrict':
      default:
        return [
          {label: 'of Total State Population', value: 'selected_of_state_total'},
          {label: 'of Total Cong. District Population', value: 'selected_of_congressional_district'}
        ]
    }
  }

  const percentOptions = getPercentOptions(selectedGeoBoundary)

  const handleUnifiedViewChange = (value) => {
    if (value === 'counts') {
      setSelectedGradientView('population')
    } else {
      setSelectedGradientView('percentage')
      setSelectedPopulationViewMode(value)
    }
  }

  useEffect(() => {
    if (selectedGradientView === 'percentage') {
      const opts = getPercentOptions(selectedGeoBoundary)
      const validValues = opts.map((o) => o.value)
      if (!validValues.includes(selectedPopulationViewMode)) {
        setSelectedPopulationViewMode(opts[0].value)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGeoBoundary])

  useEffect(() => {
    !selectedFocusLayer && setFocusLayer(isDeliveryAdmin(), dataAggregationValue, updateFocusLayer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataAggregationValue])

  useEffect(() => {
    if (isChildCareDisabled()) {
      setDisablePoiFlag(true)
      setSelectedPOI(selectedPOI.filter((data) => data !== 'childCare'))
    } else {
      setDisablePoiFlag(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChildCareDisabled])

  const newGeoBoundry = isDeliveryAdmin() ? geographicsBoundary : updatedGeoBoundry

  const handleChoroplethRadioChange = (value) => {
    setShowChoropleth(value === 'On')
    handleMenuClose()
  }

  const handleToggle = (event, anchorEl, setAnchorEl) => {
    handleMenuClose()
    anchorEl ? setAnchorEl(null) : setAnchorEl(event.currentTarget)
  }

  const handleMenuClose = () => {
    setLayerEL(null)
    setRegionEL(null)
    setPoiEL(null)
    setVisualEL(null)
    setFocusHoverEL(null)
    setPopulationViewEL(null)
  }
  if (!isDeliveryAdmin()) {
    if (dataAggregationValue?.includes(3)) {
      updateGeoBoundaryIfLabelMatches('State', geographicsBoundary, updatedGeoBoundry)
    }
    if (dataAggregationValue?.includes(2)) {
      updateGeoBoundaryIfLabelMatches('County', geographicsBoundary, updatedGeoBoundry)
    }
    if (dataAggregationValue?.includes(5)) {
      updateGeoBoundaryIfLabelMatches('Congressional District', geographicsBoundary, updatedGeoBoundry)
    }
    if (dataAggregationValue?.includes(6)) {
      updateGeoBoundaryIfLabelMatches('Census Tract', geographicsBoundary, updatedGeoBoundry)
    }
  }

  const handleMenuItemClick = (value, label) => {
    // Compute new value instead of using function updater (Redux doesn't support function updaters)
    const newSelectedPOI = selectedPOI.includes(value)
      ? selectedPOI.filter((item) => item !== value)
      : [...selectedPOI, value]
    setSelectedPOI(newSelectedPOI)
    !selectedPOI?.includes(value) &&
      AnalyticsHandler.onFilterApply({
        name: label,
        category: 'POI'
      })
  }

  const renderPOIMenuItem = (poi) => {
    // Compute enabled instead of mutating the poi object
    const enabled = !(poi.value === 'childCare' && disablePoiFlag)
    const menuItem = (
      <StyledMenuItem
        key={poi.label}
        value={poi.value}
        disabled={!enabled}
        onClick={() => enabled && handleMenuItemClick(poi.value, poi.label)}
      >
        <StyledCheckBox size='small' checked={enabled && selectedPOI?.indexOf(poi.value) > -1} />
        <StyledListItemText primary={poi.customLabel ? poi.customLabel : poi.label} disableTypography />
        <Legend
          alt={`${poi.label} icon`}
          src={getPOIImageUrl(poiConfig?.[poi.value]?.markerProps?.iconURL)}
          classes={{
            img: 'img'
          }}
        />
      </StyledMenuItem>
    )
    return enabled ? (
      menuItem
    ) : (
      <StyledTooltip key={poi.label} value={poi.value} arrow title={tooltipText.sdohTooltipChildCareDisabled}>
        <div>{menuItem}</div>
      </StyledTooltip>
    )
  }

  const layerOptions = [
    {
      label: 'On',
      value: 'On'
    },
    {
      label: 'Off',
      value: 'Off'
    }
  ]

  const menuStyleProps = {
    elevation: 0,
    sx: {
      overflow: 'visible',
      borderRadius: (theme) => theme.spacing(1),
      paddingLeft: (theme) => theme.spacing(2),
      paddingRight: (theme) => theme.spacing(2),
      marginLeft: (theme) => theme.spacing(1)
    }
  }

  const getViewerVisualOptions = () => {
    if (isDeliveryAdmin() || (dataAggregationValue?.includes(1) && dataAggregationValue?.includes(4)))
      return visualOptions
    else if (dataAggregationValue?.includes(1)) return [visualOptions[1]]
    else if (dataAggregationValue?.includes(4)) return [visualOptions[0]]
    else return visualOptions
  }

  const handleFocusLayerChange = (focusType) => {
    updateFocusLayer(focusType)
  }

  const isFocusLayerEnabled = useMemo(
    () => (currentUniqueAddressesCount ? currentUniqueAddressesCount <= uniqueAddressMaximum : false),
    [currentUniqueAddressesCount, uniqueAddressMaximum]
  )

  return (
    <>
      {showPopulationDensityTable && newGeoBoundry?.length > 1 && (
        <StyledDiv>
          <div style={{display: 'flex'}}>
            <FormControl>
              <StyledTextField
                size='small'
                id='outlined-select-currency'
                select
                SelectProps={{
                  sx: {
                    height: '32px',
                    borderRadius: '4px',
                    color: (theme) => theme.palette.hpBlack,
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: (theme) => theme.palette.hpPrimary
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      border: `1px solid ${(theme) => theme.palette.hpPrimary}`
                    }
                  }
                }}
                value={selectedGeoBoundary}
                onChange={(event) => {
                  setSelectedGeoBoundary(event.target.value)
                  AnalyticsHandler.onMapViewSelect(event.target.value)
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start' data-testid='geoSelector'>
                      <StyledText>View By:</StyledText>
                    </InputAdornment>
                  )
                }}
                inputProps={{'aria-labelledby': 'outlined-select-currency'}}
              >
                {newGeoBoundry?.map((geographicBoundary) => (
                  <MenuItem
                    aria-label={`${geographicBoundary.label}`}
                    key={geographicBoundary.label}
                    value={geographicBoundary.value}
                    sx={{
                      fontSize: (theme) => theme.typography.subtitle1.fontSize,
                      fontWeight: (theme) => theme.typography.fontWeightRegular,
                      '&.Mui-selected': {
                        backgroundColor: (theme) => theme.palette.hpPrimaryLightest,
                        '&:hover': {
                          backgroundColor: (theme) => theme.palette.hpPrimaryLightest
                        }
                      }
                    }}
                  >
                    <ListItemText
                      aria-label={`view-by-${geographicBoundary.label}`}
                      primary={geographicBoundary.label}
                      sx={{
                        fontSize: (theme) => theme.typography.subtitle1.fontSize,
                        fontWeight: (theme) => theme.typography.fontWeightRegular
                      }}
                      disableTypography
                      data-testId={`${geographicBoundary.label}`}
                    />
                  </MenuItem>
                ))}
              </StyledTextField>
            </FormControl>
          </div>
        </StyledDiv>
      )}
      {!showPopulationDensityTable && (
        <StyledStack>
          {newGeoBoundry?.length > 1 && (
            <FormControl size='small'>
              <StyledBox
                onClick={(event) => {
                  handleToggle(event, regionEL, setRegionEL)
                }}
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    handleToggle(event, regionEL, setRegionEL)
                  }
                }}
                isOpen={Boolean(regionEL)}
              >
                <PublicOutlinedIcon />
                <StyledSpan>Region</StyledSpan>
              </StyledBox>
              <MenuWithRadioButton
                anchorEl={regionEL}
                open={Boolean(regionEL)}
                label='View Region By'
                options={newGeoBoundry}
                onClick={(value) => {
                  setSelectedGeoBoundary(value)
                  let view
                  if (value === 'congressionaldistrict') {
                    view = 'Congressional District'
                  } else if (value === 'censustract') {
                    view = 'Census Tract'
                  } else {
                    view = value.charAt(0).toUpperCase() + value.slice(1)
                  }
                  selectedGeoBoundary !== value && AnalyticsHandler.onMapViewSelect(view)
                }}
                selectedValue={selectedGeoBoundary}
                onClose={handleMenuClose}
                menuStyleProps={menuStyleProps}
              />
            </FormControl>
          )}
          <FormControl size='small'>
            <StyledBox
              onClick={(event) => {
                handleToggle(event, populationViewEL, setPopulationViewEL)
              }}
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleToggle(event, populationViewEL, setPopulationViewEL)
                }
              }}
              isOpen={Boolean(populationViewEL)}
            >
              <CountPercentageIcon />
              <StyledSpan>Values</StyledSpan>
            </StyledBox>
            <Menu
              id='population-view-menu'
              anchorEl={populationViewEL}
              open={Boolean(populationViewEL)}
              onClose={handleMenuClose}
              transformOrigin={{horizontal: 'top', vertical: 'right'}}
              anchorOrigin={{horizontal: 'right', vertical: 'top'}}
              slotProps={{paper: menuStyleProps}}
              sx={{'&.MuiPopover-root': {zIndex: 10}}}
              disableEnforceFocus
            >
              <StyledTypographyForMenu variant='labelLarge'>Show Selected Population as:</StyledTypographyForMenu>
              <StyledMenuItem
                onClick={() => {
                  handleUnifiedViewChange('counts')
                  handleMenuClose()
                }}
              >
                <StyledFormControlLabel
                  aria-label='Counts'
                  value='counts'
                  label={<StyledBoldLabel>Counts (#)</StyledBoldLabel>}
                  control={<StyledRadioButton />}
                  checked={unifiedViewValue === 'counts'}
                />
              </StyledMenuItem>
              <Typography variant='labelLarge' sx={{display: 'block', pt: 1, pb: 0.5, fontWeight: 'bold'}}>
                Percent (%)
              </Typography>
              {percentOptions.map((opt) => (
                <StyledMenuItem
                  key={opt.value}
                  onClick={() => {
                    handleUnifiedViewChange(opt.value)
                    handleMenuClose()
                  }}
                >
                  <StyledFormControlLabel
                    aria-label={opt.label}
                    value={opt.value}
                    label={opt.label}
                    control={<StyledRadioButton />}
                    checked={unifiedViewValue === opt.value}
                  />
                </StyledMenuItem>
              ))}
            </Menu>
          </FormControl>
          <FormControl size='small'>
            <StyledBox
              id='point-of-interest'
              onClick={(event) => {
                handleToggle(event, poiEL, setPoiEL)
              }}
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleToggle(event, poiEL, setPoiEL)
                }
              }}
              isOpen={Boolean(poiEL)}
              inputProps={{'aria-labelledby': 'point-of-interest'}}
            >
              <PinDropRoundedIcon />

              <StyledSpan>POI</StyledSpan>
            </StyledBox>
            <Menu
              anchorEl={poiEL}
              id='poi-menu'
              open={Boolean(poiEL)}
              onClose={handleMenuClose}
              slotProps={{paper: menuStyleProps}}
              transformOrigin={{horizontal: 'top', vertical: 'right'}}
              anchorOrigin={{horizontal: 'right', vertical: 'top'}}
              sx={{'&.MuiPopover-root': {zIndex: 10}}}
              disableEnforceFocus
            >
              <StyledTypographyForMenu variant='labelLarge'>Point of Interest (POI)</StyledTypographyForMenu>
              {existingPOI &&
                existingPOI.length > 0 &&
                existingPOI.map((poi) => {
                  return renderPOIMenuItem(poi)
                })}
            </Menu>
          </FormControl>
          <FormControl size='small'>
            <StyledBox
              onClick={(event) => {
                handleToggle(event, layerEL, setLayerEL)
              }}
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  handleToggle(event, layerEL, setLayerEL)
                }
              }}
              isOpen={Boolean(layerEL)}
            >
              <StyledDivContainer>
                <StyledIconContainer>
                  <LayersIcon />
                  {!showChoropleth && <StyledOffBadge>Off</StyledOffBadge>}
                </StyledIconContainer>
                <StyledSpan>Layer</StyledSpan>
              </StyledDivContainer>
            </StyledBox>
            <MenuWithRadioButton
              anchorEl={layerEL}
              open={Boolean(layerEL)}
              label='Choropleth Layer'
              options={layerOptions}
              onClick={handleChoroplethRadioChange}
              selectedValue={showChoropleth ? 'On' : 'Off'}
              onClose={handleMenuClose}
              menuStyleProps={menuStyleProps}
            />
          </FormControl>
          {(isDeliveryAdmin() || dataAggregationValue?.includes(1) || dataAggregationValue?.includes(4)) && (
            <FormControl size='small'>
              <StyledBox
                onClick={(event) => {
                  if (isFocusLayerEnabled) {
                    handleToggle(event, visualEL, setVisualEL)
                  }
                }}
                onMouseEnter={(event) => {
                  if (!isFocusLayerEnabled) {
                    setFocusHoverEL(event.currentTarget)
                  }
                }}
                onMouseLeave={(event) => {
                  if (!isFocusLayerEnabled) {
                    setFocusHoverEL(null)
                  }
                }}
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && isFocusLayerEnabled) {
                    handleToggle(event, visualEL, setVisualEL)
                  }
                }}
                width={'auto'}
                isOpen={Boolean(visualEL)}
              >
                <StyledDivContainer>
                  {!isFocusLayerEnabled && (
                    <StyledLockBadge>
                      <StyledLockIcon fontSize='small' />
                    </StyledLockBadge>
                  )}

                  <HomeIcon />
                  <StyledSpan>Focus</StyledSpan>
                </StyledDivContainer>
              </StyledBox>
              <FocusLayerMenu
                visualEL={visualEL}
                hoverEL={focusHoverEL}
                getViewerVisualOptions={getViewerVisualOptions}
                handleFocusLayerChange={handleFocusLayerChange}
                selectedFocusLayer={selectedFocusLayer}
                handleMenuClose={handleMenuClose}
                menuStyleProps={menuStyleProps}
                isFocusLayerEnabled={isFocusLayerEnabled}
              />
            </FormControl>
          )}
        </StyledStack>
      )}
    </>
  )
}

export default PopulationDensityDropDown
